import { Form, Input } from 'antd';
import Modal from 'components/Modal';
import reportApis from 'features/report/services/apis';
import { IStaffAdjustmentSaleBody, IStaffAdjustmentSaleItem } from 'features/report/services/types/api';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IStaffItemData } from 'services/shop/types/staff';
import styled from 'styled-components';
import toast from 'utils/toast';
import StaffSearchInput from 'widgets/Staff/StaffSearchInput';
import colorTheme from 'constants/color';
import DollarAmountInput from 'components/DollarAmount/Input';
import useGetListSaleAdjustment from 'features/report/services/hooks/useGetListSaleAdjustment';

type Props = {};

type UpdateSaleAdjustmentModalRef = {
  open: (data?: IStaffAdjustmentSaleItem, staffId?: string) => void;
  close: () => void;
};

type SaleAdjustmentFormValues = {
  id: string;
  amount: number;
  billId: string;
  staffIdIncrease: string;
  staffIdDecrease: string;
  note: string | null;
};

export const useUpdateSaleAdjustmentModal = () => useRef<UpdateSaleAdjustmentModalRef>(null);

const UpdateSaleAdjustmentModal = forwardRef<UpdateSaleAdjustmentModalRef, Props>((_props, ref) => {
  const [open, setOpen] = useState(false);
  const [createdById, setCreatedById] = useState<string>();
  const { saleAdjustmentParams, mutate } = useGetListSaleAdjustment(true);
  const [form] = Form.useForm<SaleAdjustmentFormValues>();

  const onOpen: UpdateSaleAdjustmentModalRef['open'] = (data, staffId) => {
    if (data) {
      form.setFieldsValue({
        id: data.id,
        amount: data.amount,
        billId: data.billId,
        staffIdIncrease: data.staffIdIncrease,
        staffIdDecrease: data.staffIdDecrease,
        note: data.note,
      });
      setCreatedById(staffId);
    }
    setOpen(true);
  };

  const onClose = () => {
    form.resetFields();
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    close: onClose,
  }));

  const handleSubmit = async (values: any) => {
    const body: IStaffAdjustmentSaleBody = {
      id: values.id,
      amount: values.amount,
      billId: values.billId,
      staffIdIncrease: values.staffIdIncrease,
      staffIdDecrease: values.staffIdDecrease,
      note: values.note,
      createdBy: createdById,
    };

    try {
      const res = await reportApis.createSaleAdjustment(body);
      if (res?.data?.data) {
        toast.success('Create Adjust sale successfully');
        mutate(saleAdjustmentParams);
        onClose();
      }
    } catch (error) {
      toast.error('Create Adjust sale failed');
    }
  };

  return (
    <Modal
      visible={open}
      onClose={onClose}
      width={800}
      v2
      modalTitle='Update Sale Adjustment'
      noneBodyStyle={true}
      onSubmit={() => form.submit()}
      okTitle='OK'
      destroyOnClose
    >
      <Container>
        <FormStyled layout='vertical' form={form} onFinish={handleSubmit}>
          <Form.Item name='id' hidden noStyle>
            <Input />
          </Form.Item>
          <Form.Item name='billId' hidden noStyle>
            <Input />
          </Form.Item>
          <Form.Item
            name='amount'
            label='Amount'
            rules={[
              {
                required: true,
                message: 'Please enter amount',
              },
              {
                validator: (_, value) => {
                  if (value > 0) return Promise.resolve();
                  return Promise.reject(new Error('Amount must be greater than 0'));
                },
              },
            ]}
          >
            <DollarAmountInput className='input-dollar' withForm />
          </Form.Item>
          <Form.Item
            name='staffIdIncrease'
            label='Staff Sales Increase'
            rules={[{ required: true, message: 'Please choose a staff' }]}
          >
            <StaffSearchInput
              v2
              onPickAll={() => undefined}
              onPickStaff={(data: IStaffItemData | null) => {
                form.setFieldValue('staffIdIncrease', data?.id);
                form.validateFields(['staffIdIncrease']);
              }}
              showAllOption={false}
              sizeAvatar={40}
              defaultStaffId={form.getFieldValue('staffIdIncrease')}
            />
          </Form.Item>
          <Form.Item
            name='staffIdDecrease'
            label='Staff Sales Decrease'
            rules={[{ required: true, message: 'Please choose a staff' }]}
          >
            <StaffSearchInput
              v2
              onPickAll={() => undefined}
              onPickStaff={(data: IStaffItemData | null) => {
                form.setFieldValue('staffIdDecrease', data?.id);
                form.validateFields(['staffIdDecrease']);
              }}
              showAllOption={false}
              sizeAvatar={40}
              defaultStaffId={form.getFieldValue('staffIdDecrease')}
            />
          </Form.Item>
          <Form.Item name='note' label='Note'>
            <Input.TextArea rows={4} />
          </Form.Item>
        </FormStyled>
      </Container>
    </Modal>
  );
});

UpdateSaleAdjustmentModal.displayName = 'UpdateSaleAdjustmentModal';
export default UpdateSaleAdjustmentModal;

const Container = styled.div`
  display: flex;
  width: 100%;
  max-height: 70vh;
  overflow-y: hidden;
  gap: 24px;
`;

const FormStyled = styled(Form)`
  width: 100%;
  background: #f5f5f5;
  margin-bottom: 6px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 16px;

  .input-dollar {
    height: 3.5rem;
    .fake-labels {
      font-size: 20px!important;
      font-weight: 500;
      font-family: Poppins;
    }
  }

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label > label {
    font-weight: 500;
    font-size: 22px;
    font-family: Poppins;
    color: ${colorTheme.text_4_v2};
  }

  .ant-input {
    font-size: 20px!important;
    font-weight: 500;
    font-family: Poppins;
  }
`;
